import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane, MapPin, ArrowRight, ArrowUpDown, CheckCircle2, Star, Mic, Plus, X, Loader2, Square } from "lucide-react";
import { HERO, BRAND } from "../mock";
import { useLang } from "../i18n/LanguageContext";
import AddressAutocomplete from "./AddressAutocomplete";
import { DateField, TimeField } from "./PickerField";
import Reveal from "./Reveal";

const AIRPORT = "Flughafen Zürich (ZRH)";
const isAirport = (s) => /flughafen\s*z|zrh|z[uü]rich\s*airport|airport\s*z[uü]rich|kloten/i.test(s || "");
const ERR = {
  de: { addr: "Bitte geben Sie eine Zieladresse ein.", date: "Bitte wählen Sie ein Datum.", time: "Bitte wählen Sie eine Uhrzeit.", rtime: "Bitte wählen Sie Datum und Uhrzeit der Rückfahrt." },
  en: { addr: "Please enter a destination address.", date: "Please select a date.", time: "Please select a time.", rtime: "Please select the return date and time." },
  fr: { addr: "Veuillez saisir une adresse de destination.", date: "Veuillez choisir une date.", time: "Veuillez choisir une heure.", rtime: "Veuillez choisir la date et l'heure du retour." },
  it: { addr: "Inserisci un indirizzo di destinazione.", date: "Seleziona una data.", time: "Seleziona un orario.", rtime: "Seleziona data e ora del ritorno." },
};
const LAB = {
  de: { voice: "Mit Sprache buchen", listening: "Ich höre zu … tippen zum Stoppen", processing: "Sprache wird verarbeitet …", addStop: "Zwischenstopps", stopLabel: "Zwischenstopp", roundtrip: "Rückfahrt?", calc: "Preis berechnen", stopPh: "Zwischenstopp-Adresse", destPh: "Zielort eingeben", micErr: "Mikrofon-Zugriff verweigert.", voiceErr: "Sprache konnte nicht verarbeitet werden.", voiceNone: "Nichts erkannt – bitte erneut versuchen.", voiceOk: "Verstanden" },
  en: { voice: "Book by voice", listening: "Listening … tap to stop", processing: "Processing voice …", addStop: "Add stops", stopLabel: "Stop", roundtrip: "Return trip?", calc: "Calculate price", stopPh: "Stop address", destPh: "Enter destination", micErr: "Microphone access denied.", voiceErr: "Could not process voice.", voiceNone: "Nothing recognised – please try again.", voiceOk: "Understood" },
  fr: { voice: "Réserver à la voix", listening: "J'écoute … appuyez pour arrêter", processing: "Traitement de la voix …", addStop: "Arrêts", stopLabel: "Arrêt", roundtrip: "Aller-retour ?", calc: "Calculer le prix", stopPh: "Adresse de l'arrêt", destPh: "Saisir la destination", micErr: "Accès au micro refusé.", voiceErr: "Impossible de traiter la voix.", voiceNone: "Rien reconnu – réessayez.", voiceOk: "Compris" },
  it: { voice: "Prenota a voce", listening: "In ascolto … tocca per fermare", processing: "Elaborazione voce …", addStop: "Fermate", stopLabel: "Fermata", roundtrip: "Andata e ritorno?", calc: "Calcola il prezzo", stopPh: "Indirizzo fermata", destPh: "Inserisci la destinazione", micErr: "Accesso al microfono negato.", voiceErr: "Impossibile elaborare la voce.", voiceNone: "Niente riconosciuto – riprova.", voiceOk: "Capito" },
};

const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" viewBox="0 0 256 256" className={className}>
    <path d="M152.58,145.23l23,11.48A24,24,0,0,1,152,176a72.08,72.08,0,0,1-72-72A24,24,0,0,1,99.29,80.46l11.48,23L101,118a8,8,0,0,0-.73,7.51,56.47,56.47,0,0,0,30.15,30.15A8,8,0,0,0,138,155ZM232,128A104,104,0,0,1,79.12,219.82L45.07,231.17a16,16,0,0,1-20.24-20.24l11.35-34.05A104,104,0,1,1,232,128Zm-40,24a8,8,0,0,0-4.42-7.16l-32-16a8,8,0,0,0-8,.5l-14.69,9.8a40.55,40.55,0,0,1-16-16l9.8-14.69a8,8,0,0,0,.5-8l-16-32A8,8,0,0,0,104,64a40,40,0,0,0-40,40,88.1,88.1,0,0,0,88,88A40,40,0,0,0,192,152Z" />
  </svg>
);

const Hero = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const lab = LAB[lang] || LAB.de;
  const API = process.env.REACT_APP_BACKEND_URL;
  const [tab, setTab] = useState("oneway");
  const [pickup, setPickup] = useState(AIRPORT);
  const [dropoff, setDropoff] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const _now1h = new Date(Date.now() + 60 * 60 * 1000);
  _now1h.setMinutes(Math.round(_now1h.getMinutes() / 5) * 5, 0, 0);
  const _pad = (n) => String(n).padStart(2, "0");
  const defaultDate = `${_now1h.getFullYear()}-${_pad(_now1h.getMonth() + 1)}-${_pad(_now1h.getDate())}`;
  const defaultTime = `${_pad(_now1h.getHours())}:${_pad(_now1h.getMinutes())}`;
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(defaultTime);
  const [stops, setStops] = useState([]);
  const [roundtrip, setRoundtrip] = useState(false);
  const [returnDate, setReturnDate] = useState(today);
  const [returnTime, setReturnTime] = useState("");
  const [error, setError] = useState("");

  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const voiceSupported = typeof window !== "undefined" && !!(navigator.mediaDevices && window.MediaRecorder);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const swap = () => { setPickup(dropoff); setDropoff(pickup); };
  const addStop = () => setStops((s) => [...s, ""]);
  const updateStop = (i, v) => setStops((s) => s.map((x, idx) => (idx === i ? v : x)));
  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));

  const sendVoice = async (blob) => {
    setVoiceBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const res = await fetch(`${API}/api/voice/parse`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error("fail");
      if (data.destination) {
        if (data.direction === "to_airport") { setPickup(data.destination); setDropoff(AIRPORT); }
        else { setPickup(AIRPORT); setDropoff(data.destination); }
      }
      if (data.date) setDate(data.date);
      if (data.time) setTime(data.time);
      if (data.transcript) toast.success(`${lab.voiceOk}: „${data.transcript}"`);
      else toast.error(lab.voiceNone);
    } catch (e) {
      toast.error(lab.voiceErr);
    } finally {
      setVoiceBusy(false);
    }
  };

  const toggleVoice = async () => {
    if (recording) { mediaRef.current && mediaRef.current.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) await sendVoice(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(lab.micErr);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const e2 = ERR[lang] || ERR.de;
    if (!pickup.trim() || !dropoff.trim()) { setError(e2.addr); toast.error(e2.addr); return; }
    if (!date) { setError(e2.date); toast.error(e2.date); return; }
    if (!time) { setError(e2.time); toast.error(e2.time); return; }
    if (roundtrip && (!returnDate || !returnTime)) { setError(e2.rtime); toast.error(e2.rtime); return; }
    setError("");
    const params = new URLSearchParams({ from: pickup, to: dropoff, date, time, tab });
    const cleanStops = stops.map((s) => (s || "").trim()).filter(Boolean);
    if (cleanStops.length) params.set("stops", JSON.stringify(cleanStops));
    if (roundtrip) { params.set("rt", "1"); params.set("rdate", returnDate); params.set("rtime", returnTime); }
    navigate(`/booking?${params.toString()}`);
  };

  const chips = t("hero.chips");
  const fieldCls = "w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-3 py-3.5 font-body text-sm text-[#0B1533] focus:border-[#2B4BFF] focus:ring-2 focus:ring-[#2B4BFF]/15 focus:outline-none transition-all duration-200 placeholder:text-[#9CA0B0]";

  return (
    <section id="book" className="relative min-h-[100svh] flex items-start justify-center overflow-hidden bg-[#0B1533]">
      <div className="absolute inset-0">
        <img src={HERO.image} alt="Airport Taxi Zürich Chauffeur mit Namensschild am Flughafen Zürich" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1533]/75 via-[#0B1533]/80 to-[#0B1533]" />
      </div>

      <div className="relative z-10 w-full max-w-xl mx-auto px-4 sm:px-6 pt-24 pb-16 flex flex-col items-center overflow-x-hidden">
        <div className="w-full text-center mb-7 flex flex-col items-center gap-3.5">
          <Reveal><p className="font-body text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[#C9A24B]">{t("hero.kicker")}</p></Reveal>
          <Reveal delay={80}><h1 className="font-heading font-bold tracking-tight text-3xl sm:text-5xl leading-[1.1] text-white max-w-2xl">{t("hero.title")}</h1></Reveal>
          <Reveal delay={160}><p className="font-body text-sm sm:text-lg text-white/70 max-w-lg leading-relaxed">{t("hero.subtitle")}</p></Reveal>
        </div>
        <Reveal delay={220} className="w-full">
          <div className="bg-white rounded-[28px] p-4 sm:p-7 elev-lg overflow-hidden w-full max-w-full box-border">
            {/* Voice booking */}
            {voiceSupported && (
              <button type="button" onClick={toggleVoice} disabled={voiceBusy} data-testid="voice-book-btn" className={`w-full flex items-center justify-center gap-2.5 rounded-2xl py-3.5 mb-4 font-body font-semibold text-sm transition-all duration-200 ${recording ? "bg-[#C0392B] text-white animate-pulse" : "bg-[#0B1533] text-white hover:bg-[#1a2647]"} disabled:opacity-60`}>
                {voiceBusy ? (<><Loader2 size={18} className="animate-spin" /> {lab.processing}</>) : recording ? (<><Square size={16} className="fill-white" /> {lab.listening}</>) : (<><Mic size={18} className="text-[#C9A24B]" /> {lab.voice}</>)}
              </button>
            )}

            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div className="relative">
                <div className="relative">
                  <AddressAutocomplete value={pickup} onChange={setPickup} onSelect={(p) => setPickup(p.label)} placeholder={t("hero.pickupPh")} icon={Plane} className="w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-20 py-3.5 font-body text-sm text-[#0B1533] focus:border-[#2B4BFF] focus:ring-2 focus:ring-[#2B4BFF]/15 focus:outline-none transition-all duration-200 placeholder:text-[#9CA0B0]" />
                  {pickup && <button type="button" onClick={() => setPickup("")} aria-label="Clear" data-testid="hero-pickup-clear" className="absolute right-[52px] top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-[#F5F2ED] text-[#5A607A] flex items-center justify-center hover:bg-[#E9E5DE] transition-colors"><X size={14} /></button>}
                </div>
                <button type="button" onClick={swap} aria-label="Swap" data-testid="hero-swap-btn" className="absolute right-3 top-[36px] z-20 w-10 h-10 rounded-xl bg-[#0B1533] text-white flex items-center justify-center hover:bg-[#2B4BFF] transition-colors duration-200 elev-soft">
                  <ArrowUpDown size={17} />
                </button>
              </div>

              {stops.map((s, i) => (
                <div key={i} className="relative" data-testid={`hero-stop-${i}`}>
                  <div className="relative">
                    <AddressAutocomplete value={s} onChange={(v) => updateStop(i, v)} onSelect={(p) => updateStop(i, p.label)} placeholder={lab.stopPh} icon={MapPin} className="w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-12 py-3.5 font-body text-sm text-[#0B1533] focus:border-[#2B4BFF] focus:ring-2 focus:ring-[#2B4BFF]/15 focus:outline-none transition-all duration-200 placeholder:text-[#9CA0B0]" />
                    <button type="button" onClick={() => removeStop(i)} aria-label="Remove stop" data-testid={`hero-stop-remove-${i}`} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-[#F5F2ED] text-[#5A607A] flex items-center justify-center hover:bg-[#E9E5DE] transition-colors"><X size={15} /></button>
                  </div>
                </div>
              ))}

              <div>
                <AddressAutocomplete value={dropoff} onChange={setDropoff} onSelect={(p) => setDropoff(p.label)} placeholder={lab.destPh} icon={MapPin} className={fieldCls} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <DateField value={date} min={today} onChange={setDate} testid="hero-date-field" />
                </div>
                <div className="min-w-0">
                  <TimeField value={time} onChange={setTime} testid="hero-time-field" />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap pt-0.5">
                <button type="button" onClick={addStop} data-testid="hero-add-stop-btn" className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-[#0B1533] hover:text-[#2B4BFF] transition-colors">
                  <Plus size={17} className="text-[#2B4BFF]" /> {lab.addStop}
                </button>
                <button type="button" onClick={() => setRoundtrip((r) => !r)} data-testid="hero-roundtrip-btn" aria-pressed={roundtrip} className={`inline-flex items-center gap-1.5 font-body text-sm font-semibold rounded-full px-3 py-1.5 -my-1 transition-all duration-200 ${roundtrip ? "bg-[#2B4BFF] text-white shadow-sm" : "text-[#0B1533] hover:text-[#2B4BFF]"}`}>
                  <ArrowUpDown size={17} className={roundtrip ? "text-white" : "text-[#5A607A]"} /> {lab.roundtrip}
                </button>
              </div>

              {roundtrip && (
                <div className="grid grid-cols-2 gap-3" data-testid="hero-return-row">
                  <div className="min-w-0"><DateField value={returnDate} min={date || today} onChange={setReturnDate} testid="hero-return-date-field" /></div>
                  <div className="min-w-0"><TimeField value={returnTime} onChange={setReturnTime} testid="hero-return-time-field" /></div>
                </div>
              )}

              <button type="submit" data-testid="hero-book-btn" className="group w-full flex items-center justify-center gap-2 bg-[#0B1533] text-white font-body font-semibold text-base rounded-2xl py-4 hover:bg-[#1a2647] transition-all duration-200 elev-soft">
                {lab.calc}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              {error && <p className="font-body text-sm text-[#C0392B] text-center" data-testid="hero-error">{error}</p>}
            </form>
          </div>
        </Reveal>

        <div className="w-full text-center mt-10 flex flex-col items-center gap-6">
          <Reveal delay={80}>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2.5 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300 rounded-full py-3 px-8 font-body font-semibold">
              <WhatsAppIcon size={20} /> {t("hero.whatsapp")}
            </a>
          </Reveal>

          <Reveal delay={380} className="w-full max-w-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {chips.map((chip) => (
                <div key={chip} className="flex items-center gap-3 bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 text-left">
                  <CheckCircle2 size={20} className="text-[#25D366] shrink-0" />
                  <span className="font-body text-sm text-white font-medium">{chip}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={440} className="w-full max-w-lg">
            <div className="flex items-center gap-4 bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4">
              <div className="flex gap-1 shrink-0">
                {[...Array(5)].map((_, i) => (<Star key={i} size={18} className="text-[#25D366] fill-[#25D366]" />))}
              </div>
              <div className="text-left">
                <p className="font-heading font-semibold text-white text-sm">{t("hero.trustTitle")}</p>
                <p className="font-body text-xs text-white/60">{t("hero.trustSub")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Hero;
